import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  DollarSign, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, recentRes, revenueRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-invoices'),
        api.get('/dashboard/revenue-chart')
      ]);
      setStats(statsRes.data);
      setRecentInvoices(recentRes.data);
      setRevenueData(revenueRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
      pending: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
      overdue: 'bg-rose-100 text-rose-700 hover:bg-rose-100'
    };
    return styles[status] || styles.pending;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your invoicing activity</p>
        </div>
        <Link to="/invoices/new">
          <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <Card className="metric-card animate-fade-in stagger-1" data-testid="stat-revenue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Revenue</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.total_revenue)}</div>
            <p className="text-xs text-slate-500 mt-1">From {stats?.paid_count || 0} paid invoices</p>
          </CardContent>
        </Card>

        <Card className="metric-card animate-fade-in stagger-2" data-testid="stat-pending">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pending</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.pending_amount)}</div>
            <p className="text-xs text-slate-500 mt-1">{stats?.pending_count || 0} invoices awaiting payment</p>
          </CardContent>
        </Card>

        <Card className="metric-card animate-fade-in stagger-3" data-testid="stat-paid">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Paid</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{stats?.paid_count || 0}</div>
            <p className="text-xs text-slate-500 mt-1">{formatCurrency(stats?.paid_amount)} collected</p>
          </CardContent>
        </Card>

        <Card className="metric-card animate-fade-in stagger-4" data-testid="stat-overdue">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Overdue</CardTitle>
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.overdue_amount)}</div>
            <p className="text-xs text-slate-500 mt-1">{stats?.overdue_count || 0} invoices overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Recent */}
      <div className="dashboard-grid">
        {/* Revenue Chart */}
        <Card className="dashboard-card" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-[#4F46E5]" />
              Revenue Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar dataKey="revenue" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state py-12">
                <div className="empty-state-icon">
                  <TrendingUp className="w-8 h-8 text-slate-400" />
                </div>
                <p className="empty-state-title">No revenue data yet</p>
                <p className="empty-state-text">Create and mark invoices as paid to see your revenue chart</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="dashboard-card" data-testid="recent-invoices">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-[#4F46E5]" />
              Recent Invoices
            </CardTitle>
            <Link to="/invoices" className="text-sm text-[#4F46E5] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentInvoices.length > 0 ? (
              <div className="space-y-3">
                {recentInvoices.map((invoice) => (
                  <div 
                    key={invoice.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">
                        {invoice.client_name || 'Unknown Client'}
                      </p>
                      <p className="text-xs text-slate-500 font-mono">{invoice.invoice_number}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusBadge(invoice.status)}>
                        {invoice.status}
                      </Badge>
                      <span className="text-sm font-semibold text-slate-900">
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <div className="empty-state-icon">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <p className="empty-state-title">No invoices yet</p>
                <p className="empty-state-text">Create your first invoice to get started</p>
                <Link to="/invoices/new" className="mt-4">
                  <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Invoice
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
